import { useCallback, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 400;

export default function useDebouncedSearchInput(
  appliedValue: string,
  onApply: (value: string) => void,
): [string, (value: string) => void] {
  const [input, setInput] = useState(appliedValue);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastApplied = useRef(appliedValue);

  const onApplyRef = useRef(onApply);
  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);

  useEffect(() => {
    if (appliedValue !== lastApplied.current) {
      lastApplied.current = appliedValue;
      if (timer.current) clearTimeout(timer.current);
      setInput(appliedValue);
    }
  }, [appliedValue]);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const handleChange = useCallback((value: string) => {
    setInput(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      lastApplied.current = value;
      onApplyRef.current(value);
    }, DEBOUNCE_MS);
  }, []);

  return [input, handleChange];
}
